import { useState, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useProductMode } from '../contexts/ProductModeContext'
import LoginModal from './LoginModal'
import { getUserProfileImageUrl, getUserInitials } from '../utils/imageUtils'

interface HeaderProps {
  role?: 'student' | 'admin' | 'tutor' | 'school' | 'affiliate'
}

const Header = ({ role }: HeaderProps) => {
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const { isLoggedIn, logout, user } = useAuthStore()
  const { humanTutorLayerEnabled } = useProductMode()
  const location = useLocation()
  const isDashboardPage = location.pathname !== '/'
  const isTutorLoginPage = location.pathname === '/tutor'
  const isSchoolLoginPage = location.pathname === '/school'
  const isAdminLoginPage = location.pathname === '/admin' || location.pathname === '/admin/login'
  const isAnyLoginPage = isTutorLoginPage || isSchoolLoginPage || isAdminLoginPage
  
  // Use useMemo to prevent unnecessary re-renders and flashing
  const currentRole = useMemo(() => {
    return role || (user ? user.role : 'student')
  }, [role, user?.role])

  // Memoize the profile image URL to ensure it updates when user changes
  const profileImageUrl = useMemo(() => {
    return getUserProfileImageUrl(user)
  }, [user?.photoUrl, user?.photo])

  // Debug logging to see auth state changes
  useEffect(() => {
    console.log('Header Auth State:', { isLoggedIn, user, currentRole, location: location.pathname })
    console.log('Header User Photo URL:', user?.photoUrl)
    console.log('Header User Photo:', user?.photo)
    console.log('Header getUserProfileImageUrl result:', profileImageUrl)
  }, [isLoggedIn, user, currentRole, location.pathname, profileImageUrl])

  // Force re-render when auth state changes
  useEffect(() => {
    // This ensures the header updates when auth state changes
  }, [isLoggedIn, user])
  


  const handleLogoClick = () => {
    if (isAnyLoginPage) {
      // On any login page, redirect to home page
      window.location.href = '/'
    } else if (location.pathname !== '/') {
      window.location.href = '/' // Force full page reload to reset typewriter
    }
  }

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault()
    console.log('Header: Logging out user')
    
    // Show loading state immediately
    const loadingOverlay = document.createElement('div')
    loadingOverlay.id = 'logout-loading-overlay'
    loadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.95);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      font-family: Arial, sans-serif;
    `
    loadingOverlay.innerHTML = `
      <div style="text-align: center;">
        <div style="width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 20px;"></div>
        <p style="color: #333; font-size: 16px; margin: 0;">Logging out...</p>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `
    document.body.appendChild(loadingOverlay)
    
    // Clear auth state
    logout()
    setIsProfileDropdownOpen(false)
    
    // Small delay to ensure state is cleared, then redirect
    setTimeout(() => {
      window.location.href = '/'
    }, 300)
  }

  // Function to get initials from name
  const getUserInitials = (name: string) => {
    if (!name) return 'U';
    const names = name.trim().split(' ').filter(n => n.length > 0);
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    } else if (names.length === 1) {
      return names[0][0].toUpperCase();
    }
    return 'U';
  }





  // Get dashboard link based on current role
  const getDashboardLink = () => {
    switch (currentRole) {
      case 'admin':
        return '/admin/dashboard'
      case 'tutor':
        return '/instructor-dashboard'
      case 'school':
        return '/school-dashboard'
      default:
        return humanTutorLayerEnabled ? '/my-tutor' : '/messages'
    }
  }

  // Get dashboard text based on current role
  const getDashboardText = () => {
    switch (currentRole) {
      case 'admin':
        return 'ADMIN DASHBOARD'
      case 'tutor':
        return 'TUTOR DASHBOARD'
      case 'school':
        return 'SCHOOL DASHBOARD'
      default:
        return 'MY DASHBOARD'
    }
  }

  // Get financial button text and link based on current role
  const getFinancialButton = () => {
    switch (currentRole) {
      case 'admin':
        return { text: 'FINANCIALS', link: '/admin/financial' }
      case 'tutor':
        return { text: 'EARNINGS', link: '/tutor/earnings' }
      case 'school':
        return { text: 'FINANCIALS', link: '/school/financials' }
      default:
        return humanTutorLayerEnabled
          ? { text: 'MY PAYMENTS', link: '/my-payments' }
          : null
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isProfileDropdownOpen) {
        const target = event.target as Element
        if (!target.closest('.profile-dropdown-container') && !target.closest('.profile-dropdown')) {
          setIsProfileDropdownOpen(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isProfileDropdownOpen])

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isProfileDropdownOpen) {
        setIsProfileDropdownOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isProfileDropdownOpen])

  if (isDashboardPage) {
    // Dashboard header
    return (
      <>
        <header className="main-header">
          <div className="header-left">
            <span className="logo" id="overview-logo" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
              ZIMARK EDUCATION
            </span>
          </div>
          <div className="header-center">
            {/* Search functionality hidden in current version */}
          </div>
          <div className="header-right">
            {/* Show when NOT logged in and NOT on any login page */}
            {!isLoggedIn && !isAnyLoginPage && (
              <div id="guest-navigation" className="guest-navigation">
                <a href="#" className="header-link" id="nav-login" onClick={(e) => { e.preventDefault(); setIsLoginModalOpen(true); }}>
                  Login/Register
                </a>
              </div>
            )}
            
            {/* Show when logged in */}
            {isLoggedIn && (
              <>
                <Link to={getDashboardLink()} className="header-link">{getDashboardText()}</Link>
                {getFinancialButton() && (
                  <Link to={getFinancialButton()!.link} className="header-link">{getFinancialButton()!.text}</Link>
                )}
                <div className="profile-dropdown-container">
                  <span 
                    className="user-avatar" 
                    id="profile-trigger"
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  >
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl}
                        alt={user?.role === 'school' && user?.schoolName 
                          ? user.schoolName
                          : user ? `${user.firstName} ${user.lastName}` : 'User'
                        }
                        className="w-full h-full object-cover rounded-full"
                        onError={(e) => {
                          // If image fails to load, show initials instead
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          const parent = target.parentElement
                          if (parent) {
                            parent.innerHTML = user?.role === 'school' && user?.schoolName 
                              ? getUserInitials(user.schoolName)
                              : user ? getUserInitials(`${user.firstName} ${user.lastName}`) : 'U'
                          }
                        }}
                      />
                    ) : (
                      user?.role === 'school' && user?.schoolName 
                        ? getUserInitials(user.schoolName)
                        : user ? getUserInitials(`${user.firstName} ${user.lastName}`) : 'U'
                    )}
                  </span>
                  {isProfileDropdownOpen && (
                    <div className="profile-dropdown" id="profile-dropdown">
                      <div className="profile-name">
                        {user?.role === 'admin' && user?.schoolName 
                          ? user.schoolName 
                          : user?.role === 'school' && user?.schoolName
                          ? user.schoolName
                          : user ? `${user.firstName} ${user.lastName}` : 'User'
                        }
                      </div>
                      {user?.role === 'student' && (
                        <Link to="/account-settings" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>My Profile</Link>
                      )}
                      {user?.role === 'tutor' ? (
                        <Link to="/tutor/messages" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>Messages</Link>
                      ) : (
                        <Link to="/messages" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>Messages</Link>
                      )}
                      {user?.role === 'tutor' && (
                        <Link to="/instructor-dashboard" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>Tutor Dashboard</Link>
                      )}
                      {user?.role === 'admin' && (
                        <Link to="/admin/dashboard" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>Admin Dashboard</Link>
                      )}
                      {user?.role === 'school' && (
                        <Link to="/school-dashboard" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>School Dashboard</Link>
                      )}
                      <a href="#" className="dropdown-item">Invite My Friends</a>
                      <a href="#" className="dropdown-item" onClick={handleLogout}>Logout</a>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </header>
        <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      </>
    )
  }

  // Home page header
  return (
    <>
      <header className="main-header">
        <div className="header-left">
          <span className="logo" id="home-logo" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
            ZIMARK EDUCATION
          </span>
        </div>
        
        <div className="header-center">
          {/* Search functionality hidden in current version */}
        </div>
        
        <div className="header-right">
          {/* Show when NOT logged in and NOT on any login page */}
          {!isLoggedIn && !isAnyLoginPage && (
            <div id="guest-navigation" className="guest-navigation">
              <a href="#" className="header-link" id="nav-login" onClick={(e) => { e.preventDefault(); setIsLoginModalOpen(true); }}>
                Login/Register
              </a>
            </div>
          )}
          
                      {/* Show when logged in */}
            {isLoggedIn && (
              <div id="user-navigation" className="user-navigation">
                <Link to={getDashboardLink()} className="header-link">{getDashboardText()}</Link>
                {getFinancialButton() && (
                  <Link to={getFinancialButton()!.link} className="header-link">{getFinancialButton()!.text}</Link>
                )}
                <div className="profile-dropdown-container">
                <span className="user-avatar" id="profile-trigger" onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}>
                  {profileImageUrl ? (
                    <img
                      src={profileImageUrl}
                      alt={user?.role === 'school' && user?.schoolName 
                        ? user.schoolName
                        : user ? `${user.firstName} ${user.lastName}` : 'User'
                      }
                      className="w-full h-full object-cover rounded-full"
                      onError={(e) => {
                        // If image fails to load, show initials instead
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                        const parent = target.parentElement
                        if (parent) {
                          parent.innerHTML = user?.role === 'school' && user?.schoolName 
                            ? getUserInitials(user.schoolName)
                            : user ? getUserInitials(`${user.firstName} ${user.lastName}`) : 'U'
                        }
                      }}
                    />
                  ) : (
                    user?.role === 'school' && user?.schoolName 
                      ? getUserInitials(user.schoolName)
                      : user ? getUserInitials(`${user.firstName} ${user.lastName}`) : 'U'
                  )}
                </span>
                {isProfileDropdownOpen && (
                  <div className="profile-dropdown" id="profile-dropdown">
                    <div className="profile-name">
                      {user?.role === 'admin' && user?.schoolName 
                        ? user.schoolName 
                        : user?.role === 'school' && user?.schoolName
                        ? user.schoolName
                        : user ? `${user.firstName} ${user.lastName}` : 'User'
                      }
                    </div>
                    {user?.role === 'student' && (
                      <Link to="/account-settings" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>My Profile</Link>
                    )}
                    {user?.role === 'tutor' ? (
                      <Link to="/tutor/messages" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>Messages</Link>
                    ) : (
                      <Link to="/messages" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>Messages</Link>
                    )}
                    {user?.role === 'tutor' && (
                      <Link to="/tutor/dashboard" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>Tutor Dashboard</Link>
                    )}
                    {user?.role === 'admin' && (
                      <Link to="/admin/dashboard" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>Admin Dashboard</Link>
                    )}
                    {user?.role === 'school' && (
                      <Link to="/school/dashboard" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>School Dashboard</Link>
                    )}
                    <a href="#" className="dropdown-item">Invite My Friends</a>
                    <a href="#" className="dropdown-item" id="logout-btn" onClick={handleLogout}>Logout</a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>
      
      {/* Login Modal */}
      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={() => setIsLoginModalOpen(false)} 
      />
    </>
  )
}

export default Header
